
import withLink, { WithLinkProps } from 'hoc/withLink';
import { StyleSheet, Text, View } from 'react-native';
import { useFocusEffect, useLinkTo } from '@react-navigation/native';
import React, { Dispatch, SetStateAction, useRef } from 'react';
import { AgeRatingKey } from 'utils/ageRating';
import { calcSizeForDevice, calcSizeForTv, getUrlPath } from 'utils/index';
import { Platform } from 'utils/platform';

import NewGradientButton from 'components/Button/NewGradienteButton';
import FocusView from 'components/FocusView';
import HeroImage from 'components/Hero/HeroImage';
import CarouselGradient from 'components/NewCarrousel/carouselGradient';
import Rating from 'components/Rating';
import Skeleton from 'components/Skeleton';
import VideoPreview from 'components/VideoPreview';
import { WebDiv } from 'components/WebDiv';

type CarouselItemProps = {
  item: CmsHighlight;
  index?: number;
  currentIndex?: number;
  highlights?: CmsApiHightlightList;
  scroll?: any;
  loading: boolean;
  isCarouselFocused?: boolean;
  setIsCarouselFocused?: Dispatch<SetStateAction<boolean>>;
};

const CarouselItem: React.FC<CarouselItemProps> = React.memo(
  ({ item, currentIndex, loading, setIsCarouselFocused }) => {
    const linkTo = useLinkTo();
    const redirectLink = getUrlPath(item?.more?.link || '');
    const carouselRef = React.useRef(null);
    const [isCarouselBtnFocused, setIsCarouselBtnFocused] = React.useState(false);
    const [isPreviewPlaying, setIsPreviewPlaying] = React.useState(false);
    const buttonRef = useRef();

    const videoPreviewUrl = item?.videoPreview;

    // TODO: Migrar lógica de breakpoints para hook customizado se necessário
    const paddingX = calcSizeForDevice(24);
    const titleSize = calcSizeForDevice(36, 48);
    const descriptionSize = calcSizeForDevice(16, 24);
    const contentOffset = calcSizeForDevice(64);

    React.useEffect(() => {
      setIsPreviewPlaying(false);

      const timer = setTimeout(() => {
        setIsPreviewPlaying(true);
      }, 1500);

      return () => clearTimeout(timer);
    }, [currentIndex, isCarouselBtnFocused]);

    React.useEffect(() => {
      setIsCarouselFocused?.(isCarouselBtnFocused);
    }, [isCarouselBtnFocused]);

    React.useEffect(() => {
      if (Platform.isWebTV) {
        const observer = new MutationObserver((mutations: any) => {
          let isFocused = false;

          for (const mutation of mutations) {
            if (mutation.target.classList.contains('focused')) {
              isFocused = true;
            }
          }

          setIsCarouselBtnFocused(isFocused);
        });

        if (carouselRef.current) {
          observer.observe(carouselRef.current, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['class'],
          });
        }

        return () => observer.disconnect();
      }
    }, [carouselRef]);

    useFocusEffect(
      React.useCallback(() => {
        if (Platform.isAndroidTV) {
          buttonRef?.current?.requestTVFocus();
        }
      }, [])
    );

    if (loading) return <Skeleton variant="hero" />;

    return (
      <View style={styles.root}>
        <View style={styles.backgroundLayer}>
          {videoPreviewUrl && isPreviewPlaying && isCarouselBtnFocused ? (
            <VideoPreview
              videoPreview={videoPreviewUrl}
              poster={item.image?.url!}
              isPlaying={isPreviewPlaying}
              setIsPlaying={setIsPreviewPlaying}
            />
          ) : (
            !!item.image?.url && <HeroImage title={item.title!} source={item.image?.url!} />
          )}
          <CarouselGradient />
        </View>

        <View style={[styles.contentWrapper, { padding: paddingX }]}>
          <View style={[styles.column, { paddingTop: contentOffset }]}>
            <Text
              style={[
                styles.title,
                { fontSize: titleSize, lineHeight: calcSizeForDevice(32, 60) },
              ]}>
              {item?.title}
            </Text>
            <Rating ageRatingKey={item.rating as AgeRatingKey} />
            <Text
              style={[
                styles.description,
                {
                  fontSize: descriptionSize,
                  lineHeight: calcSizeForDevice(16, 32),
                  marginVertical: calcSizeForDevice(5, 15),
                },
              ]}>
              {item?.description}
            </Text>
            {item?.more?.title && (
              <WebDiv ref={carouselRef}>
                <FocusView>
                  <View style={styles.buttonBox}>
                    <NewGradientButton
                      ref={buttonRef}
                      useTVFocus
                      hasTVPreferredFocus
                      onBtnFocus={() => setIsCarouselBtnFocused?.(true)}
                      onBtnBlur={() => setIsCarouselBtnFocused?.(false)}
                      style={styles.gradientButton}
                      onPress={() => linkTo(redirectLink)}>
                      <Text style={styles.buttonText}>{item?.more?.title || 'Assistir agora'}</Text>
                    </NewGradientButton>
                  </View>
                </FocusView>
              </WebDiv>
            )}
          </View>
        </View>
      </View>
    );
  }
);

const styles = StyleSheet.create({
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  backgroundLayer: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 1,
  },
  contentWrapper: {
    zIndex: 3,
    width: '100%',
  },
  column: {
    flexDirection: 'column',
    gap: calcSizeForDevice(8, 16),
  },
  title: {
    color: '#fff',
    fontWeight: '600',
  },
  description: {
    color: '#fff',
  },
  buttonBox: {
    width: calcSizeForTv(300),
  },
  gradientButton: {
    height: calcSizeForTv(54),
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonText: {
    fontWeight: 'bold',
    color: '#070d31',
    textTransform: 'uppercase',
    fontSize: calcSizeForTv(16),
  },
});

export default function CarouselItemProxy(props: CarouselItemProps & WithLinkProps) {
  if (Platform.isTV) return <CarouselItem {...props} />;
  const Component = withLink(CarouselItem, 'Hero');
  return <Component {...props} />;
}
